import turnoverData from '@/data/turnover_rates.json';

export interface TurnoverRateData {
  industries: Array<{ key: string; name: string }>;
  metadata: {
    unit: string;
    definition: string;
    notes: string;
  };
  default: {
    average: number;
    industries: Record<string, number>;
  };
  data: Record<string, {
    basis: string;
    average: number;
    industries: Record<string, number | null>;
    sources: string[];
  }>;
}

/**
 * Get the turnover rates data
 */
function getTurnoverData(): TurnoverRateData {
  return turnoverData as TurnoverRateData;
}

/**
 * Get available industries with their friendly names
 */
export function getAvailableIndustries(): Array<{ key: string; name: string }> {
  const data = getTurnoverData();
  return data.industries;
}

/**
 * Detect country from browser locale (client-side fallback)
 */
export function detectCountryCode(): string {
  if (typeof window === 'undefined') {
    return 'default';
  }
  
  try {
    // Try to get country from browser locale
    const locale = navigator.language || navigator.languages?.[0] || 'en-US';
    const region = locale.split('-')[1]?.toUpperCase();
    
    // Map common regions to country codes that we have data for
    const supportedCountries = ['US', 'AU'];
    if (region && supportedCountries.includes(region)) {
      return region;
    }
    
    return 'default';
  } catch (error) {
    console.warn('Failed to detect country client-side:', error);
    return 'default';
  }
}

/**
 * Get the average turnover rate for a specific country
 * Falls back to default if country not found
 */
export function getCountryAverageTurnoverRate(countryCode?: string): number {
  const data = getTurnoverData();
  const country = countryCode || detectCountryCode();
  
  // Check if we have data for this country
  if (country !== 'default' && data.data[country]) {
    return data.data[country].average;
  }
  
  // Fall back to default
  return data.default.average;
}

/**
 * Get industry-specific turnover rate for a country and industry
 * Falls back through: country industry → country average → default industry → default average
 */
export function getIndustryTurnoverRate(
  industryKey: string, 
  countryCode?: string
): number {
  const data = getTurnoverData();
  const country = countryCode || detectCountryCode();
  
  // Try to get country-specific industry rate
  if (country !== 'default' && data.data[country]) {
    const countryData = data.data[country];
    const industryRate = countryData.industries[industryKey];
    
    // If we have a valid rate for this industry in this country, use it
    if (industryRate !== null && industryRate !== undefined) {
      return industryRate;
    }
    
    // Otherwise, fall back to country average
    return countryData.average;
  }
  
  // Fall back to default industry rate
  if (data.default.industries[industryKey]) {
    return data.default.industries[industryKey];
  }
  
  // Final fallback to default average
  return data.default.average;
}

/**
 * Convert decimal rate to whole number for display
 * e.g. 8.7 → 9, 8.1 → 8
 */
export function convertRateToWholeNumber(rate: number): number {
  return Math.round(rate);
}

/**
 * Get suggested turnover rate for simple calculator
 * Uses country average as the suggestion
 */
export function getSuggestedTurnoverRateForSimpleCalculator(countryCode?: string): number {
  const averageRate = getCountryAverageTurnoverRate(countryCode);
  return convertRateToWholeNumber(averageRate);
}

/**
 * Get suggested turnover rate for advanced calculator with industry selection
 * Uses industry-specific rate for the country, or country average if no industry
 */
export function getSuggestedTurnoverRateForAdvancedCalculator(
  industryKey: string | undefined,
  countryCode?: string
): number {
  if (!industryKey) {
    // If no industry selected, use country average
    return getSuggestedTurnoverRateForSimpleCalculator(countryCode);
  }
  
  const industryRate = getIndustryTurnoverRate(industryKey, countryCode);
  return convertRateToWholeNumber(industryRate);
}

/**
 * Get the exact decimal rate for backend calculations
 * This preserves the precision for calculations while showing whole numbers in UI
 */
export function getExactTurnoverRate(
  industryKey?: string,
  countryCode?: string
): number {
  if (industryKey) {
    return getIndustryTurnoverRate(industryKey, countryCode);
  }
  return getCountryAverageTurnoverRate(countryCode);
}

/**
 * Get exact decimal turnover rate for advanced calculator
 * Returns precise decimal rate, not rounded
 */
export function getExactTurnoverRateForAdvancedCalculator(
  industryKey: string | undefined,
  countryCode?: string
): number {
  if (!industryKey) {
    // If no industry selected, use country average
    return getCountryAverageTurnoverRate(countryCode);
  }
  
  return getIndustryTurnoverRate(industryKey, countryCode);
}

/**
 * Get country name for display purposes
 */
export function getCountryName(countryCode: string): string {
  const countryNames: Record<string, string> = {
    'US': 'United States',
    'AU': 'Australia',
    'default': 'Global Default'
  };
  
  return countryNames[countryCode] || 'Unknown';
}